import { useCallback, useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { api, type DemoSystem } from '../lib/api';
import { ThemeToggle } from '../components/ThemeToggle';

/**
 * The dedicated, full-screen console session — opened in its own tab from a system card. An xterm.js
 * terminal bridged over a WebSocket to `podman exec -it <container> sh` on the daemon (see
 * daemon/src/shell.ts). `mc` comes pre-wired to the system's admin creds (the `local` alias), so the
 * shell is immediately useful; `?mc=0` opens a bare shell for the "configure your own client" exercise.
 *
 * Local (`local-podman`) systems only — the button that opens this is gated the same way, and the
 * daemon rejects anything else. Cloud parity would reuse this exact page + WS frame protocol (binary =
 * terminal bytes; text = JSON control) against a different server; see the cloud-parity runbook.
 */
export function ShellPage() {
  const params = new URLSearchParams(window.location.search);
  const systemId = params.get('system') ?? '';
  const bare = params.get('mc') === '0';

  const [sys, setSys] = useState<DemoSystem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'connecting' | 'open' | 'closed'>('connecting');
  // The system's containers (primary + peers/nodes/sidecars) and which one this session targets.
  // Empty node = the daemon's default (primary); resolved to a concrete name once containers load.
  const [containers, setContainers] = useState<{ name: string; role: string; running: boolean }[]>([]);
  const [node, setNode] = useState(() => params.get('node') ?? '');
  // Bumped to force a reconnect (remounts the terminal effect) — e.g. on a node switch.
  const [attempt, setAttempt] = useState(0);

  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const { systems } = await api.systems();
        const found = systems.find((s) => s.id === systemId) ?? null;
        if (!found) throw new Error('System not found');
        setSys(found);
        document.title = `Shell — ${found.name}`;
        if (found.backend !== 'local-podman') throw new Error('Console sessions are available for local (podman) systems only.');
        if (found.state !== 'running') throw new Error('Start the system before opening a console session.');
        // Populate the node switcher; default to the primary if the URL didn't pin a node.
        const { containers } = await api.systemContainers(systemId).catch(() => ({ containers: [] }));
        setContainers(containers);
        setNode((cur) => cur || containers.find((c) => c.role === 'primary')?.name || containers[0]?.name || found.id);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'failed to load');
      }
    })();
  }, [systemId]);

  // Switch the shell to another container: pin it in the URL (shareable/refreshable) and reconnect.
  const changeNode = useCallback(
    (name: string) => {
      setNode(name);
      const u = new URL(window.location.href);
      u.searchParams.set('node', name);
      window.history.replaceState({}, '', u);
      setAttempt((a) => a + 1);
    },
    [],
  );

  // The terminal + socket lifecycle. Re-runs on reconnect (attempt) and once the system is known good.
  useEffect(() => {
    if (error || !sys || sys.backend !== 'local-podman' || sys.state !== 'running') return;
    if (!node) return; // wait until the target container is resolved (avoids a connect→reconnect flicker)
    const host = hostRef.current;
    if (!host) return;

    setStatus('connecting');
    const term = new Terminal({
      convertEol: false,
      cursorBlink: true,
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      fontSize: 13,
      theme: {
        // Fixed dark palette — matches the ops transcript (--dp-term / --term-* tokens), both themes.
        background: '#060d1a',
        foreground: '#cbd5e1',
        cursor: '#38bdf8',
        selectionBackground: 'rgba(56,189,248,0.3)',
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host);
    fit.fit();

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const q = new URLSearchParams();
    if (bare) q.set('mc', '0');
    if (node) q.set('node', node);
    const qs = q.toString();
    const url = `${proto}://${window.location.host}/api/systems/${encodeURIComponent(systemId)}/shell${qs ? `?${qs}` : ''}`;
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    const encoder = new TextEncoder();

    const sendResize = () => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
    };

    ws.onopen = () => {
      setStatus('open');
      fit.fit();
      sendResize();
      term.focus();
    };
    ws.onmessage = (ev) => {
      if (ev.data instanceof ArrayBuffer) term.write(new Uint8Array(ev.data));
      else term.write(String(ev.data));
    };
    ws.onclose = () => setStatus('closed');
    ws.onerror = () => setStatus('closed');

    // Keystrokes → binary frames (the daemon treats binary as terminal input, text as JSON control).
    const dataSub = term.onData((d) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(encoder.encode(d));
    });

    const onResize = () => {
      try {
        fit.fit();
      } catch {
        /* terminal not measurable yet */
      }
      sendResize();
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(host);
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      ro.disconnect();
      dataSub.dispose();
      try {
        ws.close();
      } catch {
        /* already closed */
      }
      term.dispose();
    };
  }, [sys, error, systemId, bare, node, attempt]);

  const reconnect = useCallback(() => setAttempt((a) => a + 1), []);

  return (
    <div className="shellpage">
      <header className="dp-top">
        <div className="dp-top-main">
          <span className="dp-kicker">MinIO AIStor · Console session{bare ? ' · bare' : ''}</span>
          <h1>{sys ? sys.name : 'Console'}</h1>
          <p className="muted dp-sub">
            {error
              ? error
              : sys
                ? `sh in ${node || sys.id}${bare ? ' — mc not pre-configured (set up your own client)' : ' — mc ready (alias “local”)'}`
                : 'Loading…'}
          </p>
        </div>
        <div className="dp-top-side">
          {containers.length > 1 && (
            <label className="shell-node">
              <span className="muted small-note">node</span>
              <select value={node} onChange={(e) => changeNode(e.target.value)} aria-label="Container">
                {containers.map((c) => (
                  <option key={c.name} value={c.name} disabled={!c.running}>
                    {c.role}
                    {c.running ? '' : ' (stopped)'}
                  </option>
                ))}
              </select>
            </label>
          )}
          <span className={`pill ${status === 'open' ? 'ok' : status === 'closed' ? 'off' : 'warn'}`}>
            {status === 'open' ? 'connected' : status === 'closed' ? 'closed' : 'connecting'}
          </span>
          <div className="dp-links">
            {status === 'closed' && !error && (
              <button type="button" className="btn small" onClick={reconnect}>
                Reconnect
              </button>
            )}
            <ThemeToggle />
            <a className="btn ghost small" href="/">Dashboard</a>
          </div>
        </div>
      </header>

      {error ? (
        <p className="error" style={{ padding: '2rem' }}>
          {error} — <a href="/">back to dashboard</a>
        </p>
      ) : (
        <div className="shell-body">
          <div className="shell-term" ref={hostRef} />
          {status === 'closed' && (
            <div className="shell-overlay">
              Session closed.{' '}
              <button type="button" className="link" onClick={reconnect}>
                Reconnect
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
